import React from 'react';
import { Box, Typography } from '@mui/material';
import FlightIcon from '@mui/icons-material/Flight';
import { BRAND_COLORS } from '../../styles/theme';
import { useTranslation } from '../../i18n';
import { LegacyHours } from '../../types/api';
import { hasLegacyHours, formatTotalPilotHours, formatLegacyBreakdown } from './pilotHours';

interface PilotHoursCardProps {
  totalPilotHours?: number | null;
  legacyFlightHours?: LegacyHours | null;
}

/**
 * Pilot flight-hours summary (tracked + legacy breakdown) for the User Profile
 * page (STD-014 split out of UserProfilePage.tsx). Renders nothing unless there
 * is a tracked total or at least one legacy bucket — matching the original
 * inline guard.
 */
export const PilotHoursCard: React.FC<PilotHoursCardProps> = ({
  totalPilotHours,
  legacyFlightHours,
}) => {
  const { t } = useTranslation();

  const hasTracked = totalPilotHours !== undefined && totalPilotHours !== null;
  const showLegacy = hasLegacyHours(legacyFlightHours);

  if (!hasTracked && !showLegacy) return null;

  return (
    <Box
      sx={{
        mt: 2,
        p: 1.5,
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
        borderRadius: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <FlightIcon sx={{ color: BRAND_COLORS.forestGreen.main }} />
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {t('profile.totalPilotHours')}:{' '}
          <Typography component="span" sx={{ fontWeight: 700 }}>
            {formatTotalPilotHours(totalPilotHours, legacyFlightHours)} h
          </Typography>
        </Typography>
      </Box>

      {/* Legacy hours breakdown */}
      {showLegacy && (
        <Box sx={{ mt: 1, ml: 4 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Tracked: {(totalPilotHours || 0).toFixed(2)} h
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Legacy: {formatLegacyBreakdown(legacyFlightHours)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
